import { api } from './client'
import type { MarketSegment } from './nomenclatoare'

// ---------- tipuri — oglindesc DTO-urile din Cipral.Application/Comercial/ClientModels.cs ----------

export type PartnerAddressType = 'Livrare' | 'Facturare'

export interface ClientAddress {
  id: string | null
  type: PartnerAddressType
  line: string
  city: string | null
  county: string | null
  postalCode: string | null
  country: string | null
  isDefault: boolean
}

export interface ClientContact {
  id: string | null
  name: string
  jobTitle: string | null
  phone: string | null
  email: string | null
}

export interface ClientListItem {
  id: string
  code: string
  name: string
  cui: string | null
  marketSegment: MarketSegment | null
  salesAgentName: string | null
  creditLimit: number | null
  isActive: boolean
}

export interface ClientDetail {
  id: string
  code: string
  name: string
  cui: string | null
  regCom: string | null
  headOfficeAddress: string | null
  headOfficeCity: string | null
  headOfficeCounty: string | null
  headOfficeCountry: string | null
  marketSegment: MarketSegment | null
  notes: string | null
  paymentTermDays: number | null
  preferredCurrency: string | null
  standardDiscountPercent: number | null
  skontoPercent: number | null
  salesAgentUserId: string | null
  priceListId: string | null
  creditLimit: number | null
  isActive: boolean
  addresses: ClientAddress[]
  contacts: ClientContact[]
}

export type SaveClientPayload = Omit<ClientDetail, 'id' | 'isActive'>

export interface SalesAgentRef { id: string; fullName: string }

export const listClients = (search?: string) =>
  api.get<ClientListItem[]>('/comercial/clienti', { params: search ? { search } : undefined }).then((r) => r.data)

export const getClient = (id: string) => api.get<ClientDetail>(`/comercial/clienti/${id}`).then((r) => r.data)

export const listSalesAgents = () => api.get<SalesAgentRef[]>('/comercial/clienti/agenti-vanzari').then((r) => r.data)

export const createClient = (payload: SaveClientPayload) =>
  api.post<{ id: string }>('/comercial/clienti', payload).then((r) => r.data)

export const updateClient = (id: string, payload: SaveClientPayload) =>
  api.put(`/comercial/clienti/${id}`, payload)

export const deactivateClient = (id: string) => api.post(`/comercial/clienti/${id}/deactivate`)
export const reactivateClient = (id: string) => api.post(`/comercial/clienti/${id}/reactivate`)

// ---------- prețuri negociate (§6.4 MDP, FR-MDP-002) ----------

export interface ClientPriceOverride {
  id: string
  articleId: string
  articleCode: string
  articleDescription: string
  price: number
  currency: string
  effectiveFrom: string
  effectiveTo: string | null
}

export type SaveClientPriceOverridePayload = Omit<ClientPriceOverride, 'id' | 'articleCode' | 'articleDescription'>

export const listClientPriceOverrides = (clientId: string) =>
  api.get<ClientPriceOverride[]>(`/comercial/clienti/${clientId}/preturi-negociate`).then((r) => r.data)

export const addClientPriceOverride = (clientId: string, payload: SaveClientPriceOverridePayload) =>
  api.post<{ id: string }>(`/comercial/clienti/${clientId}/preturi-negociate`, payload).then((r) => r.data)

export const updateClientPriceOverride = (overrideId: string, payload: SaveClientPriceOverridePayload) =>
  api.put(`/comercial/clienti/preturi-negociate/${overrideId}`, payload)

// ---------- produse uzuale (FR-MDC-007) ----------

export interface ClientUsualArticle {
  id: string
  articleId: string
  articleCode: string
  articleDescription: string
}

export const listClientUsualArticles = (clientId: string) =>
  api.get<ClientUsualArticle[]>(`/comercial/clienti/${clientId}/produse-uzuale`).then((r) => r.data)

export const addClientUsualArticle = (clientId: string, articleId: string) =>
  api.post<{ id: string }>(`/comercial/clienti/${clientId}/produse-uzuale`, { articleId }).then((r) => r.data)

export const removeClientUsualArticle = (usualArticleId: string) =>
  api.delete(`/comercial/clienti/produse-uzuale/${usualArticleId}`)

// ---------- sold expus în timp real (FR-MDC-005/006) ----------

export interface ClientBalance {
  unpaidInvoicesTotal: number
  pendingOrdersTotal: number
  totalExposure: number
  creditLimit: number | null
  availableCredit: number | null
}

export const getClientBalance = (clientId: string) => api.get<ClientBalance>(`/comercial/clienti/${clientId}/sold`).then((r) => r.data)
